import { useCallback, useEffect, useRef } from 'react';
import { PPT_WORKER_STATUS, PPTWorkerExportMessage } from '../../../models/pptx.model';
import { useDispatch, useSelector } from 'react-redux';
import { useMultiTileSplitExportPPT } from '../../widgets/widget-hooks/useClientSideExportPPT';
import {
  selectIsExportRunningInBackground,
  selectShowProgressBar,
  setExportProgress,
  setRunningInBackground,
  setShowExportProgressBar,
} from '../../../store/slices/export-ppt.slice';
import { useModuleProjectContext } from '../../../contexts/useModuleProjectContext';
import { NotificationType, ToastState } from 'common/models/notification.model';
import { showToast } from 'common/store/slices/notification.slice';
import { USER_ROUTES } from 'routes/routes.const';
import { PROJECT_ID_PLACEHOLDER } from 'common/constants';

const WATCHDOG_MS = 60_000; // Watchdog timeout in milliseconds (e.g., 60 seconds); Adjust as needed

export function PowerpointExportServiceWorkerListener() {
  const dispatch = useDispatch();
  const isRunningInBackground = useSelector(selectIsExportRunningInBackground);
  const isExportVisible = useSelector(selectShowProgressBar);
  const isExporting = isRunningInBackground || isExportVisible;
  const { exportMultiTilePPT } = useMultiTileSplitExportPPT();
  const { activeModule, activeProject } = useModuleProjectContext();

  // --- Watchdog state ---
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKickRef = useRef<number | null>(null);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const kickWatchdog = useCallback(() => {
    // record the time we received some activity
    lastKickRef.current = Date.now();
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      // If still exporting and we haven't seen READY, consider it stuck
      if (isExporting) {
        // Show toast notification to user
        const toast: ToastState = {
          id: 'ppt-export-stuck-toast',
          title: 'PowerPoint Export Failed',
          text: 'The export process appears to be stuck. Please try again.',
          type: NotificationType.ERROR,
        };
        dispatch(showToast(toast));

        // Try to tell the service worker to abort (optional, safe no-op if not handled)
        try {
          if (navigator?.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'ABORT_EXPORT' });
          }
        } catch {
          // ignore
        }

        // Reset UI/export state
        dispatch(setShowExportProgressBar(false));
        dispatch(setRunningInBackground(false));
        dispatch(setExportProgress(0));
      }
    }, WATCHDOG_MS);
  }, [clearWatchdog, dispatch, isExporting]);

  // Start/stop the watchdog whenever exporting state changes
  useEffect(() => {
    if (isExporting) {
      kickWatchdog(); // start the timer when export begins
    } else {
      clearWatchdog(); // stop when not exporting
    }
    return clearWatchdog;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExporting]);

  // Guard against leaving while exporting
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (isExporting) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [isExporting]);

  // SW message listener + watchdog kicks
  useEffect(() => {
    const listener = (event: { data: PPTWorkerExportMessage }) => {
      const { type, progress, data } = event.data;

      // Any message means the pipeline is alive — reset watchdog
      kickWatchdog();

      switch (type) {
        case PPT_WORKER_STATUS.PROGRESS: {
          dispatch(setExportProgress(progress));
          // store interim data if you need to persist across screens
          break;
        }
        case PPT_WORKER_STATUS.ERROR: {
          // Show error toast
          const toast: ToastState = {
            id: 'ppt-export-error-toast',
            title: 'PowerPoint Export Error',
            text: 'An error occurred during export. Please try again.',
            type: NotificationType.ERROR,
          };
          dispatch(showToast(toast));

          // Reset state on fatal errors
          dispatch(setShowExportProgressBar(false));
          dispatch(setRunningInBackground(false));
          // keep progress or clear:
          // dispatch(setExportProgress(0));
          break;
        }
        case PPT_WORKER_STATUS.READY: {
          // Export is complete on the worker side; stop watchdog
          clearWatchdog();

          dispatch(setShowExportProgressBar(false));
          dispatch(setRunningInBackground(false));
          const projectId = activeProject?.moduleProjectID ?? '';
          exportMultiTilePPT(data)
            .then((allSlidesExported) => {
              dispatch(setExportProgress(0));
              if (allSlidesExported) {
                dispatch(
                  showToast({
                    id: 'ppt-export-notification',
                    text: `Your Powerpoint is now downloading. Once completed,
          <br />
          you can access it from 'My Files' page`,
                    hasButton: true,
                    buttonLabel: 'View',
                    buttonLink: USER_ROUTES.files.replace(PROJECT_ID_PLACEHOLDER, projectId),
                    type: NotificationType.INFO,
                    newTab: true,
                  })
                );
              }
            })
            .catch((error) => {
              const toast: ToastState = {
                id: 'add-to-report-toast',
                title: 'Error while exporting PowerPoint.',
                text: error?.data?.message || 'Please try again later.',
                type: NotificationType.ERROR,
              };

              dispatch(showToast(toast));
            });
          // .catch((error) => console.error('Error generating PowerPoint:', error));
          break;
        }
        case PPT_WORKER_STATUS.EXPORT_ABORTED: {
          // Show warning toast for export aborted
          dispatch(
            showToast({
              id: 'export-aborted-toast',
              text: event.data.message || 'Export was aborted.',
              type: NotificationType.WARNING,
            })
          );

          // Reset state after abort
          dispatch(setShowExportProgressBar(false));
          dispatch(setRunningInBackground(false));
          dispatch(setExportProgress(0));
          break;
        }
      }
    };

    if (navigator?.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', listener);
    }

    return () => {
      if (navigator?.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', listener);
      }
      clearWatchdog();
    };
  }, [activeModule, activeProject, clearWatchdog, dispatch, exportMultiTilePPT, kickWatchdog]);
  return null;
}
